import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { format, subDays } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import {
  useAuthUserRestaurant,
  useConfirmAllProductOfferOrdersMutation,
  useGetAllProductOfferOrdersQuery,
} from '@durma-soft/gros-sdk';
import { ProductOfferOrderWithRelations } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

import { Spinner } from '@/components/shared/spinner';
import { DataTable } from '@/components/shared/data-table';
import { PrintButton } from '@/components/shared/print-button';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';
import {
  RestaurantOrdersGroup,
  orderTotalsByCurrency,
} from '@/components/restaurant/order-overview/restaurant-orders-group';
import { ClosedOrdersModal } from '@/components/restaurant/order-overview/closed-orders-modal';

import print from '@/lib/print/print';

import { allOrderColumns } from '@/config/restaurant/platform-product-offers';

const RestaurantOrderOverview = () => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [pendingSorting, setPendingSorting] = useState<SortingState>([]);
  const [isClosedOrdersOpen, setIsClosedOrdersOpen] = useState(false);

  const { from, to, setDateRange } = useDatePickerRangeState({
    from: subDays(new Date(), 13),
  });

  const pendingOrders = useGetAllProductOfferOrdersQuery({
    restaurant_id: restaurant.id,
    status: 'pending',
  });
  const confirmedOrders = useGetAllProductOfferOrdersQuery({
    restaurant_id: restaurant.id,
    status: 'confirmed',
    start_date: from,
    end_date: to,
  });

  const confirmAll = useConfirmAllProductOfferOrdersMutation();

  const columns = useMemo(() => allOrderColumns(t), [t]);

  const groups = useMemo(() => {
    const map = new Map<
      number,
      { id: number; name: string; orders: ProductOfferOrderWithRelations[] }
    >();
    for (const order of confirmedOrders.data ?? []) {
      const group = map.get(order.restaurant.id);
      if (group) group.orders.push(order);
      else
        map.set(order.restaurant.id, {
          id: order.restaurant.id,
          name: order.restaurant.name,
          orders: [order],
        });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [confirmedOrders.data]);

  const handleConfirmAll = async () => {
    try {
      await confirmAll.mutateAsync({ restaurant_id: restaurant.id });
      toast.success(t('product-offers.confirm-all-success-msg'));
    } catch {
      toast.error(t('product-offers.confirm-all-error-msg'));
    }
  };

  const handlePrint = () =>
    print({
      title: t('order-overview.title'),
      content: (
        <div>
          <h1>{t('order-overview.confirmed-title')}</h1>
          <p>
            {format(new Date(from), 'dd.MM.yyyy')} -{' '}
            {format(new Date(to), 'dd.MM.yyyy')}
          </p>
          {groups.map((group) => (
            <div key={group.id} className="print-in-separate-page">
              <h2>{group.name}</h2>
              <table>
                <thead>
                  <tr>
                    <th>{t('common.date')}</th>
                    <th>{t('product-offers.ordered-by')}</th>
                    <th>{t('common.name')}</th>
                    <th>{t('product-offers.supplier')}</th>
                    <th>{t('common.quantity')}</th>
                    <th>{t('product-offers.price-per-unit')}</th>
                    <th>{t('product-offers.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        {format(new Date(order.created_at), 'dd.MM.yyyy HH:mm')}
                      </td>
                      <td>
                        {order.user.first_name} {order.user.last_name}
                      </td>
                      <td>{order.product_offer.name}</td>
                      <td>{order.product_offer.supplier?.name ?? '—'}</td>
                      <td>
                        {Number(order.quantity)} {order.unit}
                      </td>
                      <td>
                        {Number(order.price_per_unit).toFixed(2)}{' '}
                        {order.currency}
                      </td>
                      <td>
                        {(
                          Number(order.price_per_unit) * Number(order.quantity)
                        ).toFixed(2)}{' '}
                        {order.currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6}>{t('product-offers.total')}</td>
                    <td>{orderTotalsByCurrency(group.orders).join(' + ')}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
        </div>
      ),
    });

  const hasPending = (pendingOrders.data ?? []).length > 0;

  return (
    <>
      <Helmet>
        <title>{t('order-overview.title')}</title>
      </Helmet>
      <div className="flex flex-col gap-8">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('order-overview.title')}
        </h1>
        {(hasPending || pendingOrders.isError) && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold md:text-xl">
                {t('order-overview.pending-title')}
              </h2>
              {hasPending && (
                <Button
                  variant="outline"
                  isLoading={confirmAll.isPending}
                  onClick={handleConfirmAll}
                >
                  {t('product-offers.confirm-all')}
                </Button>
              )}
            </div>
            {pendingOrders.isError ? (
              <p className="p-4 text-center text-sm text-destructive">
                {t('common.error')}
              </p>
            ) : (
              <DataTable
                columns={columns}
                data={pendingOrders.data ?? []}
                sorting={pendingSorting}
                setSorting={setPendingSorting}
              />
            )}
          </div>
        )}
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold md:text-xl">
            {t('order-overview.confirmed-title')}
          </h2>
          <div className="flex flex-col gap-3 lg:flex-row">
            <div className="flex-1 flex flex-col gap-3 lg:flex-row">
              <div className="self-start">
                <DatePickerWithRange
                  date={{ from: new Date(from), to: new Date(to) }}
                  setDate={([from, to]) => {
                    setDateRange({ from, to });
                  }}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setIsClosedOrdersOpen(true)}
              >
                {t('order-overview.closed-orders')}
              </Button>
            </div>
            <div className="justify-start lg:justify-end flex">
              <PrintButton
                disabled={confirmedOrders.isLoading || groups.length === 0}
                onClick={handlePrint}
              />
            </div>
          </div>
          {confirmedOrders.isLoading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : confirmedOrders.isError ? (
            <p className="p-4 text-center text-sm text-destructive">
              {t('common.error')}
            </p>
          ) : groups.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              {t('order-overview.empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-10">
              {groups.map((group) => (
                <RestaurantOrdersGroup
                  key={group.id}
                  name={group.name}
                  orders={group.orders}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {isClosedOrdersOpen && (
        <ClosedOrdersModal onClose={() => setIsClosedOrdersOpen(false)} />
      )}
    </>
  );
};

export default RestaurantOrderOverview;
